import { parseChatMarkdown } from "@jugl-web/domain-resources/chat-bot/utils/parseChatMarkdown";
import { formatTicketUserName } from "@jugl-web/domain-resources/tickets";
import { useTicketComments } from "@jugl-web/domain-resources/tickets/hooks/useTicketComments";
import { getTicketCommentText } from "@jugl-web/domain-resources/tickets/utils/getTicketCommentText";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { TicketCommentModel } from "@jugl-web/rest-api/tickets/models/TicketComment";
import { TicketDetailsModel } from "@jugl-web/rest-api/tickets/models/TicketDetails";
import { useCommentAttachmentPreview } from "@jugl-web/domain-resources/comments";
import { DataLoadingWrapper, Menu, PlainButton } from "@jugl-web/ui-components";
import {
  isoToLocalDate,
  PaginationComponent,
  PaginationComponentHandle,
  useToast,
  useTranslations,
} from "@jugl-web/utils";
import { useLoadedMe } from "@web-src/features/app/hooks/useMe";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { CommentListItem } from "./components/CommentListItem";
import { TicketCommentInput } from "./components/TicketCommentInput";
import { ReactComponent as MenuIcon } from "./assets/menu.svg";
import { ReactComponent as DeleteIcon } from "./assets/delete.svg";
import { ReactComponent as EditIcon } from "./assets/edit.svg";

export type TeamNotesTabContentProps = {
  ticket: TicketDetailsModel;
  onTotalEntriesChange: (totalEntries: number) => void;
};

export const TeamNotesTabContent: FC<TeamNotesTabContentProps> = ({
  ticket,
  onTotalEntriesChange,
}) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { entityId } = useEntitySelectedProvider();
  const { meId } = useLoadedMe();
  const { ticketsApi } = useRestApiProvider();
  const { handleAttachmentClick } = useCommentAttachmentPreview();
  const paginationRef = useRef<PaginationComponentHandle | null>(null);
  const shouldScrollRef = useRef(false);

  const [editingComment, setEditingComment] =
    useState<TicketCommentModel | null>(null);
  const [deleteComment] = ticketsApi.useDeleteCommentMutation();

  const {
    data: comments,
    totalEntries,
    loadMore,
    isError,
    isLoading,
    retry,
    isLoadingMoreError,
    addComment,
    updateComment,
    removeComment,
  } = useTicketComments({
    entityId,
    ticketId: ticket.id,
    pageSize: 20,
    skip: !entityId,
  });

  const commentItems = useMemo(
    () =>
      [...comments]
        .sort(
          (a, b) =>
            new Date(a.inserted_at).getTime() -
            new Date(b.inserted_at).getTime()
        )
        .map((comment) => ({ id: comment.id, data: comment })),
    [comments]
  );

  const handleDeleteComment = async (comment: TicketCommentModel) => {
    try {
      await deleteComment({
        entityId,
        ticketId: ticket.id,
        commentId: comment.id,
      }).unwrap();

      if (editingComment?.id === comment.id) {
        setEditingComment(null);
      }

      removeComment(comment.id);
    } catch {
      toast(
        t({
          id: "common.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
    }
  };

  // Scroll to bottom when comment count changes
  useEffect(() => {
    if (!shouldScrollRef.current) {
      return undefined;
    }

    shouldScrollRef.current = false;
    const timeoutId = setTimeout(() => {
      paginationRef.current?.scrollToBottom();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [commentItems.length]);

  // Notify parent component about total entries change
  useEffect(() => {
    if (totalEntries) {
      onTotalEntriesChange(totalEntries);
    }
  }, [totalEntries, onTotalEntriesChange]);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <DataLoadingWrapper
          isError={isError}
          isLoading={isLoading && commentItems.length === 0}
          onRetry={retry}
          isLoadingMoreError={isLoadingMoreError}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
        >
          {commentItems.length === 0 ? (
            <div className="flex min-h-0 flex-1 items-center justify-center px-8 py-10 text-center">
              <span className="font-secondary text-sm leading-[160%] text-[#828282]">
                {t(
                  {
                    id: "client-support-page.empty-team-notes-message",
                    defaultMessage:
                      "Post a note to start a discussion with{br} Team. {mention} someone to notify them",
                  },
                  {
                    br: <br />,
                    mention: <span className="text-primary-800">@Mention</span>,
                  }
                )}
              </span>
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-hidden">
              <PaginationComponent
                ref={paginationRef}
                id={ticket.id}
                items={commentItems}
                isLoading={isLoading}
                endReached={loadMore}
                reverse
                extraVirtuosoParams={{
                  alignToBottom: false,
                  className: "jugl__custom-scrollbar",
                }}
                renderer={(_, item) => {
                  const comment = item.data;

                  return (
                    <CommentListItem
                      username={formatTicketUserName(comment.created_by)}
                      userImageUrl={comment.created_by.img}
                      message={parseChatMarkdown({
                        raws: getTicketCommentText(comment.content),
                      })}
                      date={isoToLocalDate(comment.inserted_at)}
                      isEdited={comment.updated_at !== comment.inserted_at}
                      attachments={comment.content.attachments}
                      onAttachmentClick={handleAttachmentClick}
                      contextMenuSlot={
                        comment.created_by.user_id === meId && (
                          <Menu
                            placement="bottom-end"
                            hasBackdrop
                            sections={[
                              [
                                {
                                  id: "edit",
                                  label: t({
                                    id: "client-support-page.edit-note",
                                    defaultMessage: "Edit Note",
                                  }),
                                  icon: <EditIcon />,
                                  onSelect: (_event, close) => {
                                    setEditingComment(comment);
                                    close();
                                  },
                                },
                                {
                                  id: "delete",
                                  label: t({
                                    id: "client-support-page.delete-note",
                                    defaultMessage: "Delete Note",
                                  }),
                                  icon: <DeleteIcon />,
                                  onSelect: (_event, close) => {
                                    handleDeleteComment(comment);
                                    close();
                                  },
                                },
                              ],
                            ]}
                            renderTrigger={({ Trigger, triggerRef }) => (
                              <Trigger as={PlainButton} ref={triggerRef}>
                                <MenuIcon />
                              </Trigger>
                            )}
                          />
                        )
                      }
                    />
                  );
                }}
              />
            </div>
          )}
        </DataLoadingWrapper>
      </div>
      <div className="shrink-0 border-0 border-t border-grey-200 bg-white p-4">
        <TicketCommentInput
          ticket={ticket}
          editingComment={editingComment}
          onEditingCancel={() => setEditingComment(null)}
          onSuccess={(comment, action) => {
            if (action === "create") {
              addComment(comment);
              shouldScrollRef.current = true;
              return;
            }

            updateComment(comment);
          }}
        />
      </div>
    </div>
  );
};
